import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { PhotoLightbox, PhotoThumb } from '@/components/ui/PhotoLightbox';
import { adminService, type AdminAttendanceStatus } from '@/services/admin';
import type { ClassSummary } from '@/types';
import { MonthlyAttendanceCalendar } from '@/components/admin/reports/MonthlyAttendanceCalendar';
import YearlyAttendanceGrid from '@/components/admin/reports/YearlyAttendanceGrid';
import { AttendanceOutOfBlock, OfficeAttendanceHeading, getAttendanceDenominator, getOfficeAttendanceDenominator, getOfficeAttendanceHeading, getOfficeAttendanceMissed, getOfficeMarksCaption, isAttendanceSingleDay, toLocalISODate } from './attendanceMetricUtils';
import { AttendanceWhoWhenBar, AttendanceAnswerFade, currentMonthToTodayRange, deriveAttendanceWhenChip, type AttendanceWhenChip, type AttendanceWhoScope } from './AttendanceWhoWhenBar';
import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, format, isWeekend, isBefore, isWithinInterval } from 'date-fns';
import { resolvePersonPhoto } from '@/utils/avatars';

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom' | 'range';

const attendanceStatusOptions: Array<{ label: string; value: AdminAttendanceStatus; className: string }> = [
    { label: 'Present', value: 'PRESENT', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    { label: 'Absent', value: 'ABSENT', className: 'border-rose-200 bg-rose-50 text-rose-700' },
    { label: 'Late', value: 'LATE', className: 'border-amber-200 bg-amber-50 text-amber-700' },
    { label: 'Leave', value: 'LEAVE', className: 'border-blue-200 bg-blue-50 text-blue-700' },
];

type TeacherAttendanceRow = {
    id: string;
    name: string;
    photo?: string | null;
    present: number;
    absent: number;
    late: number;
    leave: number;
    missed: number;
    total: number;
};

const getTeacherDraftStatus = (teacher: TeacherAttendanceRow): AdminAttendanceStatus => {
    if (teacher.present > 0) return 'PRESENT';
    if (teacher.absent > 0) return 'ABSENT';
    if (teacher.late > 0) return 'LATE';
    if (teacher.leave > 0) return 'LEAVE';
    return 'PRESENT';
};


// Sub-component for Teacher Detail Modal Content (Replicated from Student Detail)
const TeacherDetailContent = ({
    teacher,
    availableYears
}: {
    teacher: { id: string; name: string; photo?: string | null };
    availableYears: { min: number; max: number };
}) => {
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(new Date());

    const { data: historyData, isLoading } = useQuery({
        queryKey: ['teacher-attendance-history', teacher.id, format(currentDate, 'yyyy-MM')],
        queryFn: () => adminService.getTeacherAttendanceHistory(teacher.id, format(currentDate, 'yyyy-MM')),
        enabled: !!teacher.id
    });

    const currentMonthStart = startOfMonth(currentDate);
    const currentMonthEnd = endOfMonth(currentDate);
    const calendarDays = eachDayOfInterval({ start: currentMonthStart, end: currentMonthEnd });

    const records = historyData?.records || [];
    const holidays = historyData?.holidays || [];
    const summary = historyData?.summary || { present: 0, absent: 0, late: 0, leave: 0 };
    const detailExpected = getAttendanceDenominator(summary);

    const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newMonth = parseInt(e.target.value);
        const newDate = new Date(currentDate.getFullYear(), newMonth - 1, 1);
        setCurrentDate(newDate);
    };

    const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newYear = parseInt(e.target.value);
        const newDate = new Date(newYear, currentDate.getMonth(), 1);
        setCurrentDate(newDate);
    };

    return (
        <div className="space-y-6">
            {/* Navigation Controls */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="text-sm font-medium text-slate-600">Viewing History for:</div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button variant="secondary" onClick={() => navigate(`/admin/reports/teachers/${teacher.id}`)}>
                        View report
                    </Button>
                    <select
                        value={currentDate.getMonth() + 1}
                        onChange={handleMonthChange}
                        className="rounded-md border-slate-300 text-sm focus:border-brand focus:ring-brand"
                    >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                            <option key={m} value={m}>
                                {new Date(0, m - 1).toLocaleString('default', { month: 'long' })}
                            </option>
                        ))}
                    </select>
                    <select
                        value={currentDate.getFullYear()}
                        onChange={handleYearChange}
                        className="rounded-md border-slate-300 text-sm focus:border-brand focus:ring-brand"
                    >
                        {Array.from(
                            { length: availableYears.max - availableYears.min + 1 },
                            (_, i) => availableYears.max - i
                        ).map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {isLoading ? (
                <div className="h-48 flex items-center justify-center text-slate-400">Loading history...</div>
            ) : (
                <div className="flex flex-col md:flex-row gap-6">
                    {/* Left Col: Overview */}
                    <div className="md:w-1/3 space-y-6">
                        <Card>
                            <div className="text-center p-4">
                                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden ring-2 ring-slate-200">
                                    <img src={resolvePersonPhoto(teacher, 'teacher')} alt="" className="h-full w-full object-cover" />
                                </div>
                                <h2 className="text-xl font-bold text-slate-800">{teacher.name}</h2>
                                <p className="text-sm text-slate-500">Attendance Profile</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mt-6">
                                <AttendanceOutOfBlock label="Present" count={summary.present} total={detailExpected} tone="emerald" display="percent" />
                                <AttendanceOutOfBlock label="Absent" count={summary.absent} total={detailExpected} tone="rose" display="percent" />
                                <AttendanceOutOfBlock label="Late" count={summary.late} total={detailExpected} tone="amber" display="percent" />
                                <AttendanceOutOfBlock label="Leave" count={summary.leave} total={detailExpected} tone="blue" display="percent" />
                            </div>
                        </Card>
                    </div>

                    {/* Right Col: Calendar */}
                    <div className="md:w-2/3">
                        <Card title={`Calendar: ${format(currentDate, 'MMMM yyyy')}`}>
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div key={day} className="text-center text-xs font-semibold text-slate-400 py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {Array.from({ length: currentMonthStart.getDay() }).map((_, i) => (
                                    <div key={`empty-${i}`} className="aspect-square bg-transparent" />
                                ))}
                                {calendarDays.map((day) => {
                                    const dateStr = format(day, 'yyyy-MM-dd');
                                    // Check Holiday
                                    const holiday = holidays.find((h: any) =>
                                        isWithinInterval(day, { start: parseISO(h.start), end: parseISO(h.end) })
                                    );

                                    const record = records.find((r: any) => r.date === dateStr);
                                    let status = (record?.status || 'none').toLowerCase();

                                    // Use configured off-days from summary
                                    const offDays = summary.offDays || [0, 6];
                                    const isOffDay = offDays.includes(day.getDay());

                                    if (holiday || isOffDay) {
                                        status = 'holiday';
                                    }

                                    // Unmarked Logic
                                    const isUnmarked = !isOffDay && !holiday && isBefore(day, new Date()) && !isSameDay(day, new Date()) && status === 'none';

                                    let bgClass = 'bg-slate-50';
                                    let textClass = 'text-slate-400';
                                    let ringClass = '';

                                    if (status === 'present') {
                                        bgClass = 'bg-emerald-100';
                                        textClass = 'text-emerald-700 font-bold';
                                    } else if (status === 'absent') {
                                        bgClass = 'bg-rose-100';
                                        textClass = 'text-rose-700 font-bold';
                                    } else if (status === 'late') {
                                        bgClass = 'bg-amber-100';
                                        textClass = 'text-amber-700 font-bold';
                                    } else if (status === 'leave') {
                                        bgClass = 'bg-blue-100';
                                        textClass = 'text-blue-700 font-bold';
                                    } else if (status === 'holiday') {
                                        bgClass = 'bg-purple-100';
                                        textClass = 'text-purple-700 font-bold';
                                    } else if (isUnmarked) {
                                        bgClass = 'bg-slate-200';
                                        textClass = 'text-slate-500 font-medium italic';
                                    }

                                    if (isSameDay(day, new Date())) {
                                        ringClass = 'ring-2 ring-brand ring-offset-1';
                                    }

                                    let label = '';
                                    if (status === 'present') label = 'P';
                                    else if (status === 'absent') label = 'A';
                                    else if (status === 'late') label = 'L';
                                    else if (status === 'leave') label = 'Lv';
                                    else if (status === 'holiday') label = 'Off';
                                    else if (isUnmarked) label = '—';

                                    return (
                                        <div
                                            key={dateStr}
                                            className={`aspect-square rounded-lg flex flex-col items-center justify-center p-0.5 text-sm ${bgClass} ${textClass} ${ringClass} group relative overflow-hidden`}
                                            title={holiday ? holiday.name : (isWeekend(day) ? 'Weekend' : undefined)}
                                        >
                                            <span className="leading-none font-medium">{format(day, 'd')}</span>
                                            {label ? (
                                                <span className="text-[10px] font-bold leading-none truncate overflow-hidden w-full text-center mt-0.5">
                                                    {label}
                                                </span>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </div>
    );
};


export default function AdminTeacherAttendancePage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // Initialize state from URL
    const urlMonth = searchParams.get('month');
    const urlYear = searchParams.get('year');

    const [period, setPeriod] = useState<Period>('monthly');
    const [showDailyRecords, setShowDailyRecords] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState<{ id: string; name: string; photo?: string | null } | null>(null);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<'present' | 'absent' | 'late' | 'leave' | 'missed' | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGrade, setSelectedGrade] = useState<string>('');
    const [selectedClassId, setSelectedClassId] = useState<string>('');
    const [selectedSectionId, setSelectedSectionId] = useState<string>('');
    const [whoScope, setWhoScope] = useState<AttendanceWhoScope>('school');
    const [isTakeAttendanceMode, setIsTakeAttendanceMode] = useState(false);
    const [isTeacherAttendanceSetupReady, setIsTeacherAttendanceSetupReady] = useState(false);
    const [teacherAttendanceDraft, setTeacherAttendanceDraft] = useState<Record<string, AdminAttendanceStatus>>({});
    const [teacherAttendanceToast, setTeacherAttendanceToast] = useState<string | null>(null);

    const saveTeacherAttendanceMutation = useMutation({
        mutationFn: adminService.saveTeacherAttendanceBatch,
        onSuccess: async (result) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['admin-teacher-attendance-summary'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-teacher-monthly-overview'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-teacher-yearly-overview'] }),
                queryClient.invalidateQueries({ queryKey: ['teacher-attendance-history'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-school-attendance-summary-v2'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-attendance-summary'] }),
                queryClient.invalidateQueries({ queryKey: ['admin-overview'] }),
            ]);
            setTeacherAttendanceToast(`Teacher attendance saved for ${result.date}.`);
        },
    });

    useEffect(() => {
        if (!teacherAttendanceToast) return undefined;
        const timer = window.setTimeout(() => setTeacherAttendanceToast(null), 3500);
        return () => window.clearTimeout(timer);
    }, [teacherAttendanceToast]);


    // Period / Date State
    const [timeFilterMode, setTimeFilterMode] = useState<'period' | 'day'>('period');
    const [periodSubType, setPeriodSubType] = useState<'monthly' | 'yearly' | 'custom'>('monthly');
    const [availableYears, setAvailableYears] = useState<{ min: number; max: number }>({ min: new Date().getFullYear(), max: new Date().getFullYear() });
    const [selectedYear, setSelectedYear] = useState<number>(urlYear ? Number(urlYear) : new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(urlMonth ? Number(urlMonth) : new Date().getMonth() + 1);
    const [customDate, setCustomDate] = useState<string>(toLocalISODate(new Date()));
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>(currentMonthToTodayRange());

    useEffect(() => {
        if (timeFilterMode === 'day' && customDate) {
            setPeriod('daily');
            // Sync selectedYear and selectedMonth so Query matches the customDate's month
            // preventing the calendar from disappearing or showing wrong month
            const date = new Date(customDate);
            if (!isNaN(date.getTime())) {
                const newYear = date.getFullYear();
                const newMonth = date.getMonth() + 1;
                // Only update if changed to avoid unnecessary re-renders
                if (newYear !== selectedYear) setSelectedYear(newYear);
                if (newMonth !== selectedMonth) setSelectedMonth(newMonth);
            }
        } else {
            if (periodSubType === 'monthly') setPeriod('monthly');
            else if (periodSubType === 'yearly') setPeriod('yearly');
            else if (periodSubType === 'custom') setPeriod('range');
        }
    }, [timeFilterMode, periodSubType, customDate]);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const SortIcon = ({ columnKey }: { columnKey: string }) => {
        if (sortConfig?.key !== columnKey) return <span className="ml-1 text-slate-300">↕</span>;
        return sortConfig.direction === 'asc' ? <span className="ml-1 text-brand">↑</span> : <span className="ml-1 text-brand">↓</span>;
    };

    // Fetch Available Years
    const { data: yearsData } = useQuery({
        queryKey: ['available-years'],
        queryFn: adminService.getAvailableYears,
        staleTime: Infinity,
    });

    useEffect(() => {
        if (yearsData) {
            setAvailableYears(yearsData);
        }
    }, [yearsData]);

    // Fetch Classes and Sections
    const { data: classes = [] } = useQuery<ClassSummary[]>({
        queryKey: ['admin-classes'],
        queryFn: adminService.listClasses
    });

    const { data: sections = [] } = useQuery({
        queryKey: ['admin-sections'],
        queryFn: adminService.listSections
    });

    const availableSections = useMemo(() => {
        const sourceClasses = selectedGrade
            ? classes.filter((classItem) => classItem.gradeLevel === selectedGrade)
            : classes;
        const sectionMap = new Map<string, string>();

        sourceClasses.forEach((classItem) => {
            if (!classItem.sectionId) {
                return;
            }
            sectionMap.set(
                classItem.sectionId,
                sections.find((section) => section.id === classItem.sectionId)?.name || 'Unknown'
            );
        });

        if (!selectedGrade && sectionMap.size === 0) {
            sections.forEach((section) => sectionMap.set(section.id, section.name || 'Unknown'));
        }

        return Array.from(sectionMap, ([id, name]) => ({ id, name }));
    }, [classes, sections, selectedGrade]);

    const availableClasses = useMemo(() => {
        return classes.filter((classItem) => {
            const gradeMatches = !selectedGrade || classItem.gradeLevel === selectedGrade;
            const sectionMatches = !selectedSectionId || classItem.sectionId === selectedSectionId;
            return gradeMatches && sectionMatches;
        });
    }, [classes, selectedGrade, selectedSectionId]);

    const handleClassChange = (classId: string) => {
        setSelectedClassId(classId);
        const classItem = classes.find((item) => item.id === classId);
        if (classItem) {
            setSelectedGrade(classItem.gradeLevel);
            setSelectedSectionId(classItem.sectionId || '');
            return;
        }
        setSelectedGrade('');
        setSelectedSectionId('');
    };

    // Sync state with URL
    useEffect(() => {
        const params: any = {};
        if (periodSubType === 'monthly') {
            params.month = selectedMonth.toString();
            params.year = selectedYear.toString();
        }
        setSearchParams(params);
    }, [selectedMonth, selectedYear, periodSubType, setSearchParams]);

    const { data: summary, isLoading } = useQuery({
        queryKey: ['admin-teacher-attendance-summary', period, periodSubType, timeFilterMode, selectedYear, selectedMonth, customDate, dateRange, selectedGrade, selectedClassId, selectedSectionId],
        queryFn: () => {
            let params: any = { period };

            if (timeFilterMode === 'day' || period === 'daily') {
                params.period = 'daily';
                params.startDate = customDate;
                params.endDate = customDate;
            } else if (periodSubType === 'yearly' || period === 'yearly') {
                params.period = 'yearly';
                params.startDate = `${selectedYear}-01-01`;
                params.endDate = `${selectedYear}-12-31`;
            } else if (periodSubType === 'custom') {
                params.period = 'range';
                params.startDate = dateRange.start;
                params.endDate = dateRange.end;
            } else if (period === 'monthly') {
                const start = new Date(selectedYear, selectedMonth - 1, 1);
                const end = new Date(selectedYear, selectedMonth, 0);
                params.startDate = format(start, 'yyyy-MM-dd');
                params.endDate = format(end, 'yyyy-MM-dd');
            }

            if (selectedClassId) params.classId = selectedClassId;
            if (selectedSectionId) params.sectionId = selectedSectionId;
            if (selectedGrade && !selectedClassId) params.gradeLevel = selectedGrade;

            return adminService.getTeacherAttendanceSummary(params);
        },
    });



    const { data: monthlyOverview } = useQuery({
        queryKey: ['admin-teacher-monthly-overview', selectedYear, selectedMonth, selectedGrade, selectedClassId, selectedSectionId],
        queryFn: () => adminService.getTeacherMonthlyAttendanceOverview({
            year: selectedYear,
            month: selectedMonth,
            classId: selectedClassId || undefined,
            sectionId: selectedSectionId || undefined,
            gradeLevel: selectedGrade && !selectedClassId ? selectedGrade : undefined
        }),
        enabled: period === 'monthly' || periodSubType === 'custom' || timeFilterMode === 'day'
    });

    const { data: yearlyOverview, isLoading: isLoadingYearly } = useQuery({
        queryKey: ['admin-teacher-yearly-overview', selectedYear, selectedGrade, selectedClassId, selectedSectionId],
        queryFn: () => adminService.getTeacherYearlyAttendanceOverview({
            year: selectedYear,
            classId: selectedClassId || undefined,
            sectionId: selectedSectionId || undefined,
            gradeLevel: selectedGrade && !selectedClassId ? selectedGrade : undefined
        }),
        enabled: period === 'yearly'
    });

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'present': return 'bg-emerald-50 border-emerald-200 text-emerald-700';
            case 'absent': return 'bg-rose-50 border-rose-200 text-rose-700';
            case 'late': return 'bg-amber-50 border-amber-200 text-amber-700';
            case 'leave': return 'bg-blue-50 border-blue-200 text-blue-700';
            case 'missed': return 'bg-slate-50 border-slate-200 text-slate-700';
            default: return 'bg-slate-50 border-slate-200 text-slate-700';
        }
    };

    const getPercentage = (value: number, total: number) => {
        return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
    };

    const attendanceSingleDay = isAttendanceSingleDay({
        timeFilterMode,
        startDate: summary?.startDate,
        endDate: summary?.endDate,
    });
    const officeHeading = getOfficeAttendanceHeading({
        singleDay: attendanceSingleDay,
        periodSubType,
        selectedYear,
        selectedMonth,
        customDate,
        startDate: summary?.startDate,
        endDate: summary?.endDate,
    });
    const summaryExpected = summary ? getOfficeAttendanceDenominator(summary.summary, attendanceSingleDay) : 0;
    const summaryMissed = summary ? getOfficeAttendanceMissed(summary.summary, attendanceSingleDay) : 0;
    const summaryBarDenom = summaryExpected || 1;
    const summaryMarksCaption = summary
        ? getOfficeMarksCaption(summary.summary, {
            singleDay: attendanceSingleDay,
            peopleWord: 'teachers',
            unfinished: officeHeading.unfinished,
        })
        : '';
    const summaryCardDisplay = attendanceSingleDay ? 'people' : 'percent';
    const whenChip = deriveAttendanceWhenChip({ timeFilterMode, periodSubType });

    // Filter and Sort
    const filteredAndSortedTeachers = useMemo(() => {
        if (!summary?.teachers) return [];

        let result = summary.teachers.filter(t =>
            t.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            (!selectedStatus || t[selectedStatus] > 0)
        );

        if (sortConfig) {
            result.sort((a, b) => {
                let aValue: any = a[sortConfig.key as keyof typeof a];
                let bValue: any = b[sortConfig.key as keyof typeof b];

                // Handle calculated %
                if (sortConfig.key === 'percentage') {
                    const aExpected = a.total + (a.missed || 0);
                    const bExpected = b.total + (b.missed || 0);
                    aValue = parseFloat(getPercentage(a.present, aExpected));
                    bValue = parseFloat(getPercentage(b.present, bExpected));
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [summary, searchQuery, selectedStatus, sortConfig]);

    useEffect(() => {
        if (!isTakeAttendanceMode || timeFilterMode !== 'day' || !summary?.teachers) {
            return;
        }

        setTeacherAttendanceDraft((current) => {
            const next: Record<string, AdminAttendanceStatus> = {};
            summary.teachers.forEach((teacher: TeacherAttendanceRow) => {
                next[teacher.id] = current[teacher.id] ?? getTeacherDraftStatus(teacher);
            });
            return next;
        });
    }, [customDate, isTakeAttendanceMode, summary?.teachers, timeFilterMode]);

    const startTeacherAttendanceMode = () => {
        setCustomDate(toLocalISODate(new Date()));
        setTimeFilterMode('day');
        setPeriod('daily');
        setSelectedStatus(null);
        setSearchQuery('');
        setTeacherAttendanceDraft({});
        setIsTeacherAttendanceSetupReady(false);
        setIsTakeAttendanceMode(true);
        setTeacherAttendanceToast(null);
        window.setTimeout(() => {
            document.getElementById('attendance-mark-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
    };

    const cancelTeacherAttendanceMode = () => {
        setIsTakeAttendanceMode(false);
        setIsTeacherAttendanceSetupReady(false);
        setTeacherAttendanceDraft({});
    };

    const beginTeacherAttendanceSetup = () => {
        setTeacherAttendanceDraft({});
        setIsTeacherAttendanceSetupReady(true);
    };

    const setTeacherDraftStatus = (teacherId: string, status: AdminAttendanceStatus) => {
        setTeacherAttendanceDraft((current) => ({ ...current, [teacherId]: status }));
    };

    const markAllTeacherDrafts = (status: AdminAttendanceStatus) => {
        const teachers = summary?.teachers ?? [];
        setTeacherAttendanceDraft(
            teachers.reduce<Record<string, AdminAttendanceStatus>>((acc, teacher: TeacherAttendanceRow) => {
                acc[teacher.id] = status;
                return acc;
            }, {})
        );
    };

    const saveTeacherAttendance = () => {
        const teachers = summary?.teachers ?? [];
        const records = teachers.map((teacher: TeacherAttendanceRow) => ({
            id: teacher.id,
            status: teacherAttendanceDraft[teacher.id] ?? getTeacherDraftStatus(teacher),
        }));

        if (!records.length) {
            return;
        }

        saveTeacherAttendanceMutation.mutate({
            date: customDate,
            records,
        });
    };

    const handleDayClick = (date: string) => {
        setCustomDate(date);
        setTimeFilterMode('day');
        setPeriod('daily');
    };

    const applyWhoScope = (next: AttendanceWhoScope) => {
        setWhoScope(next);
        if (next === 'school') {
            setSelectedClassId('');
            setSelectedGrade('');
            setSelectedSectionId('');
        }
    };

    const applyWhoClass = (classId: string) => {
        setWhoScope('class');
        handleClassChange(classId);
    };

    const applyWhenChip = (chip: AttendanceWhenChip) => {
        const now = new Date();
        if (chip !== 'today') {
            setIsTakeAttendanceMode(false);
            setIsTeacherAttendanceSetupReady(false);
            setTeacherAttendanceDraft({});
        }
        if (chip === 'today') {
            setTimeFilterMode('day');
            setPeriod('daily');
            setCustomDate(toLocalISODate(now));
            return;
        }
        setTimeFilterMode('period');
        if (chip === 'month') {
            setPeriodSubType('monthly');
            setPeriod('monthly');
            setSelectedMonth(now.getMonth() + 1);
            setSelectedYear(now.getFullYear());
            return;
        }
        if (chip === 'year') {
            setPeriodSubType('yearly');
            setPeriod('yearly');
            setSelectedYear(now.getFullYear());
            return;
        }
    };

    return (
        <DashboardShell title="Teacher Attendance Analytics" role="admin">
            <div className="space-y-6">
                {teacherAttendanceToast && (
                    <div className="fixed bottom-6 right-6 z-50 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800 shadow-lg">
                        {teacherAttendanceToast}
                    </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Staff Attendance</h1>
                        <p className="text-sm text-slate-500">Who, when, and what happened.</p>
                    </div>
                    <Button variant="primary" onClick={startTeacherAttendanceMode}>
                        Take Attendance
                    </Button>
                </div>

                <AttendanceWhoWhenBar
                    scope={whoScope}
                    onScopeChange={applyWhoScope}
                    classId={selectedClassId}
                    onClassChange={applyWhoClass}
                    classes={classes}
                    whenChip={whenChip}
                    onWhenChip={applyWhenChip}
                    selectedMonth={selectedMonth}
                    selectedYear={selectedYear}
                    onMonthChange={setSelectedMonth}
                    onYearChange={setSelectedYear}
                    availableYears={availableYears}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                />

                {/* Summary Cards */}
                {summary && (
                    <AttendanceAnswerFade motionKey={`${officeHeading.title}:${summaryCardDisplay}:${whoScope}:${selectedClassId}`}>
                    <div>
                        <OfficeAttendanceHeading
                            title={officeHeading.title}
                            caption={attendanceSingleDay ? undefined : summaryMarksCaption}
                        />
                        <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
                        <Card
                            title="Total Teachers"
                            className="cursor-pointer hover:shadow-lg transition-shadow"
                            onClick={() => setSelectedStatus(null)}
                        >
                            <p className="text-3xl font-bold text-slate-900">{summary.summary.total || summary.teachers.length}</p>
                            <p className="text-sm text-slate-500 mt-1">
                                Active
                            </p>
                        </Card>

                        {['present', 'absent', 'late', 'leave', 'missed'].map(status => {
                            const count = status === 'missed'
                                ? summaryMissed
                                : summary.summary[status as keyof typeof summary.summary] || 0;
                            const labels = {
                                present: 'Present',
                                absent: 'Absent',
                                late: 'Late',
                                leave: 'Leave',
                                missed: 'Unmarked'
                            };
                            const tones = {
                                present: 'emerald',
                                absent: 'rose',
                                late: 'amber',
                                leave: 'blue',
                                missed: 'slate'
                            } as const;

                            return (
                                <div
                                    key={status}
                                    className="cursor-pointer"
                                    onClick={() => setSelectedStatus(status as any)}
                                >
                                    <AttendanceOutOfBlock
                                        label={labels[status as keyof typeof labels]}
                                        count={count}
                                        total={summaryExpected}
                                        tone={tones[status as keyof typeof tones]}
                                        className="p-6 shadow-sm transition-shadow hover:shadow-lg"
                                        display={summaryCardDisplay}
                                    />
                                </div>
                            );
                        })}
                        </div>
                    </div>
                    </AttendanceAnswerFade>
                )}

                {summary && (
                    <div className="px-1">
                        <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden flex">
                            {summary.summary.present > 0 && (
                                <div className="h-full bg-emerald-500" style={{ width: `${(summary.summary.present / summaryBarDenom) * 100}%` }} title={`Present: ${summary.summary.present}`} />
                            )}
                            {summary.summary.absent > 0 && (
                                <div className="h-full bg-rose-500" style={{ width: `${(summary.summary.absent / summaryBarDenom) * 100}%` }} title={`Absent: ${summary.summary.absent}`} />
                            )}
                            {summary.summary.late > 0 && (
                                <div className="h-full bg-amber-500" style={{ width: `${(summary.summary.late / summaryBarDenom) * 100}%` }} title={`Late: ${summary.summary.late}`} />
                            )}
                            {summary.summary.leave > 0 && (
                                <div className="h-full bg-blue-500" style={{ width: `${(summary.summary.leave / summaryBarDenom) * 100}%` }} title={`Leave: ${summary.summary.leave}`} />
                            )}
                            {summaryMissed > 0 && (
                                <div className="h-full bg-slate-400" style={{ width: `${(summaryMissed / summaryBarDenom) * 100}%` }} title={`Unmarked: ${summaryMissed}`} />
                            )}
                        </div>
                        <div className="flex justify-between text-xs text-slate-400 mt-1 px-1">
                            <span>0%</span>
                            <span>50%</span>
                            <span>{attendanceSingleDay ? `100% (${summaryMarksCaption})` : '100%'}</span>
                        </div>
                    </div>
                )}

                {/* Calendar / Grid View */}
                {((period === 'monthly' || period === 'yearly' || periodSubType === 'custom') || timeFilterMode === 'day') && (
                    <div className="mb-6">
                        {(period === 'yearly' && timeFilterMode !== 'day') ? (
                            <Card title="Yearly Attendance Overview">
                                {yearlyOverview ? (
                                    <YearlyAttendanceGrid
                                        data={yearlyOverview}
                                        isLoading={isLoadingYearly}
                                        onMonthClick={(month) => {
                                            setSelectedMonth(month);
                                            setPeriodSubType('monthly');
                                            setPeriod('monthly');
                                        }}
                                    />
                                ) : (
                                    <div className="text-center py-12 text-slate-500">
                                        {isLoadingYearly ? 'Loading overview...' : 'No attendance recorded yet for this period. Pick another month or take attendance first.'}
                                    </div>
                                )}
                            </Card>
                        ) : monthlyOverview ? (
                            <MonthlyAttendanceCalendar
                                data={monthlyOverview}
                                year={timeFilterMode === 'day' ? parseInt(customDate.split('-')[0]) : selectedYear}
                                month={timeFilterMode === 'day' ? parseInt(customDate.split('-')[1]) : selectedMonth}
                                isLoading={isLoading}
                                highlightDate={timeFilterMode === 'day' ? customDate : undefined}
                                onDayClick={handleDayClick}
                            />
                        ) : (
                            <Card title="Monthly Attendance Overview">
                                <div className="text-center py-12 text-slate-500">
                                    {isLoading ? 'Loading overview...' : 'No attendance recorded yet for this period. Pick another month or take attendance first.'}
                                </div>
                            </Card>
                        )}
                    </div>
                )}

                {/* Teachers List */}
                {summary && (
                    <div id="attendance-mark-list">
                    <Card title={isTakeAttendanceMode
                        ? isTeacherAttendanceSetupReady
                            ? `Take Attendance (${summary.teachers.length} teachers)`
                            : 'Take Attendance'
                        : `Attendance Records (${filteredAndSortedTeachers.length} teachers)`
                    }>
                        <div className="space-y-4">
                            {isTakeAttendanceMode && (
                                <div className="rounded-lg border border-slate-200 bg-white p-4">
                                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-900">Prepare Teacher Attendance</p>
                                            <p className="mt-1 text-sm text-slate-500">Confirm the date before opening the teacher attendance list.</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <Button variant="secondary" onClick={cancelTeacherAttendanceMode}>
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="primary"
                                                onClick={beginTeacherAttendanceSetup}
                                                disabled={!customDate}
                                            >
                                                Begin Attendance
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Date</label>
                                            <input
                                                aria-label="Teacher attendance date"
                                                type="date"
                                                value={customDate}
                                                onChange={(e) => {
                                                    setCustomDate(e.target.value);
                                                    setIsTeacherAttendanceSetupReady(false);
                                                    setTeacherAttendanceDraft({});
                                                }}
                                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30 bg-white"
                                            />
                                        </div>
                                        <div className="md:col-span-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                            {selectedGrade || selectedSectionId || selectedClassId
                                                ? `Current scope: ${selectedClassId ? availableClasses.find((classItem) => classItem.id === selectedClassId)?.name || 'Selected class' : selectedGrade || 'All grades'}${selectedSectionId ? ` / ${availableSections.find((section) => section.id === selectedSectionId)?.name || 'Selected section'}` : ''}`
                                                : 'Current scope: all teachers'}
                                        </div>
                                    </div>
                                    {isTeacherAttendanceSetupReady && (
                                        <div className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                                            Ready to take teacher attendance for {customDate}.
                                        </div>
                                    )}
                                </div>
                            )}

                            {(!isTakeAttendanceMode || isTeacherAttendanceSetupReady) && (
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <input
                                    type="text"
                                    placeholder="Search teacher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="rounded-md border-slate-300 shadow-sm focus:border-brand focus:ring-brand sm:text-sm pl-4 pr-10 py-2 w-64"
                                />
                                {isTakeAttendanceMode && isTeacherAttendanceSetupReady && (
                                    <div className="flex flex-wrap gap-2">
                                        <Button variant="ghost" onClick={() => markAllTeacherDrafts('PRESENT')} disabled={!summary.teachers.length}>
                                            Mark All Present
                                        </Button>
                                        <Button variant="ghost" onClick={() => markAllTeacherDrafts('ABSENT')} disabled={!summary.teachers.length}>
                                            Mark All Absent
                                        </Button>
                                        <Button variant="secondary" onClick={cancelTeacherAttendanceMode}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={saveTeacherAttendance}
                                            disabled={!summary.teachers.length || saveTeacherAttendanceMutation.isPending}
                                        >
                                            {saveTeacherAttendanceMutation.isPending ? 'Saving...' : 'Save Attendance'}
                                        </Button>
                                    </div>
                                )}
                            </div>
                            )}
                            {isTakeAttendanceMode && isTeacherAttendanceSetupReady && (
                                <div className="rounded-lg border border-brand/20 bg-brand/5 p-4 text-sm text-slate-600">
                                    Unmarked teachers default to Present. Saving updates existing records for {customDate}.
                                </div>
                            )}

                            {isTakeAttendanceMode ? (
                                isTeacherAttendanceSetupReady ? (
                                    <>
                                        {/* Desktop Table View */}
                                        <div className="hidden md:block overflow-x-auto">
                                            <table className="min-w-full divide-y divide-slate-200">
                                                <thead className="bg-slate-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Name</th>
                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 w-12">Photo</th>
                                                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-200 bg-white">
                                                    {filteredAndSortedTeachers.map((teacher: TeacherAttendanceRow) => {
                                                        const selectedDraftStatus = teacherAttendanceDraft[teacher.id] ?? getTeacherDraftStatus(teacher);
                                                        return (
                                                            <tr key={teacher.id} className="hover:bg-slate-50">
                                                                <td className="px-3 py-3 text-sm font-medium text-slate-900">{teacher.name}</td>
                                                                <td className="px-3 py-3">
                                                                    <PhotoThumb
                                                                        src={resolvePersonPhoto(teacher, 'teacher')}
                                                                        sizeClass="h-9 w-9"
                                                                        onClick={() => setPreviewSrc(resolvePersonPhoto(teacher, 'teacher'))}
                                                                    />
                                                                </td>
                                                                <td className="px-3 py-3">
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {attendanceStatusOptions.map((option) => (
                                                                            <button
                                                                                key={option.value}
                                                                                type="button"
                                                                                onClick={() => setTeacherDraftStatus(teacher.id, option.value)}
                                                                                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${selectedDraftStatus === option.value
                                                                                    ? option.className
                                                                                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                                                                                    }`}
                                                                            >
                                                                                {option.label}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {filteredAndSortedTeachers.length === 0 && (
                                                        <tr>
                                                            <td colSpan={3} className="px-3 py-6 text-center text-sm text-slate-500">No teachers found</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Mobile V4 Sticky Take Header + Status Pill Cards */}
                                        <div className="md:hidden space-y-3">
                                            {/* V4 Sticky Header */}
                                            <div className="bg-slate-900 text-white rounded-2xl p-3 shadow-md sticky top-2 z-20 space-y-2 border border-slate-800">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="text-[10px] font-black text-sky-400 uppercase tracking-wider">Staff Take-Mode</div>
                                                        <div className="text-xs font-bold text-white">
                                                            {summary.teachers.length} Teachers • {customDate}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => markAllTeacherDrafts('PRESENT')}
                                                            disabled={!summary.teachers.length}
                                                            className="text-[10px] font-extrabold bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white px-2.5 py-1 rounded-lg shadow-sm cursor-pointer"
                                                        >
                                                            ⚡ All P
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={saveTeacherAttendance}
                                                            disabled={!summary.teachers.length || saveTeacherAttendanceMutation.isPending}
                                                            className="text-[10px] font-extrabold bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white px-2.5 py-1 rounded-lg shadow-sm disabled:opacity-50 cursor-pointer"
                                                        >
                                                            {saveTeacherAttendanceMutation.isPending ? '...' : 'Save'}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Progress Bar */}
                                                <div className="space-y-1">
                                                    <div className="flex justify-between text-[10px] text-slate-300 font-semibold">
                                                        <span>Staff Progress</span>
                                                        <span>
                                                            {Object.keys(teacherAttendanceDraft).length} / {summary.teachers.length} Set
                                                        </span>
                                                    </div>
                                                    <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden flex shadow-inner">
                                                        <div
                                                            className="bg-emerald-500 h-full transition-all"
                                                            style={{
                                                                width: `${summary.teachers.length > 0 ? (summary.teachers.filter((t: TeacherAttendanceRow) => (teacherAttendanceDraft[t.id] ?? getTeacherDraftStatus(t)) === 'PRESENT').length / summary.teachers.length) * 100 : 0}%`
                                                            }}
                                                        />
                                                        <div
                                                            className="bg-rose-500 h-full transition-all"
                                                            style={{
                                                                width: `${summary.teachers.length > 0 ? (summary.teachers.filter((t: TeacherAttendanceRow) => (teacherAttendanceDraft[t.id] ?? getTeacherDraftStatus(t)) === 'ABSENT').length / summary.teachers.length) * 100 : 0}%`
                                                            }}
                                                        />
                                                        <div
                                                            className="bg-amber-500 h-full transition-all"
                                                            style={{
                                                                width: `${summary.teachers.length > 0 ? (summary.teachers.filter((t: TeacherAttendanceRow) => (teacherAttendanceDraft[t.id] ?? getTeacherDraftStatus(t)) === 'LATE').length / summary.teachers.length) * 100 : 0}%`
                                                            }}
                                                        />
                                                        <div
                                                            className="bg-blue-500 h-full transition-all"
                                                            style={{
                                                                width: `${summary.teachers.length > 0 ? (summary.teachers.filter((t: TeacherAttendanceRow) => (teacherAttendanceDraft[t.id] ?? getTeacherDraftStatus(t)) === 'LEAVE').length / summary.teachers.length) * 100 : 0}%`
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Mobile Teacher List with Status Pills */}
                                            <div className="space-y-2">
                                                {filteredAndSortedTeachers.map((teacher: TeacherAttendanceRow) => {
                                                    const selectedDraftStatus = teacherAttendanceDraft[teacher.id] ?? getTeacherDraftStatus(teacher);

                                                    return (
                                                        <div key={teacher.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-2.5 transition-all">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-2.5">
                                                                    <PhotoThumb
                                                                        src={resolvePersonPhoto(teacher, 'teacher')}
                                                                        sizeClass="h-9 w-9"
                                                                        onClick={() => setPreviewSrc(resolvePersonPhoto(teacher, 'teacher'))}
                                                                    />
                                                                    <div>
                                                                        <div className="text-xs font-bold text-slate-900">{teacher.name}</div>
                                                                        <div className="text-[10px] text-slate-400">Teacher</div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* V4 Status Pills: ~44px Touch Targets */}
                                                            <div className="grid grid-cols-4 gap-1.5 pt-0.5">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setTeacherDraftStatus(teacher.id, 'PRESENT')}
                                                                    className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                                                        selectedDraftStatus === 'PRESENT'
                                                                            ? 'bg-emerald-600 text-white border-emerald-600 shadow ring-2 ring-emerald-300'
                                                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                                >
                                                                    <span className="text-[11px] leading-none">✓</span>
                                                                    <span className="text-[10px] leading-tight">Pres</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setTeacherDraftStatus(teacher.id, 'ABSENT')}
                                                                    className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                                                        selectedDraftStatus === 'ABSENT'
                                                                            ? 'bg-rose-600 text-white border-rose-600 shadow ring-2 ring-rose-300'
                                                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                                >
                                                                    <span className="text-[11px] leading-none">✕</span>
                                                                    <span className="text-[10px] leading-tight">Abs</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setTeacherDraftStatus(teacher.id, 'LATE')}
                                                                    className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                                                        selectedDraftStatus === 'LATE'
                                                                            ? 'bg-amber-500 text-white border-amber-500 shadow ring-2 ring-amber-300'
                                                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                                >
                                                                    <span className="text-[11px] leading-none">⏱</span>
                                                                    <span className="text-[10px] leading-tight">Late</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setTeacherDraftStatus(teacher.id, 'LEAVE')}
                                                                    className={`h-11 rounded-xl text-xs font-bold transition-all border flex flex-col justify-center items-center cursor-pointer active:scale-95 ${
                                                                        selectedDraftStatus === 'LEAVE'
                                                                            ? 'bg-blue-600 text-white border-blue-600 shadow ring-2 ring-blue-300'
                                                                            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                                >
                                                                    <span className="text-[11px] leading-none">✈</span>
                                                                    <span className="text-[10px] leading-tight">Leave</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                {filteredAndSortedTeachers.length === 0 && (
                                                    <div className="p-6 text-center text-sm text-slate-500 bg-slate-50 rounded-xl border">
                                                        No teachers found
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center">
                                        <p className="text-sm font-semibold text-slate-800">Confirm the date to begin taking teacher attendance.</p>
                                        <p className="mt-1 text-sm text-slate-500">The teacher list and save controls will appear after you begin.</p>
                                    </div>
                                )
                            ) : (
                                <>
                                    {/* Desktop Table View */}
                                    <div className="hidden md:block overflow-x-auto">
                                        <table className="min-w-full divide-y divide-slate-200">
                                            <thead className="bg-slate-50">
                                                <tr>
                                                    {[
                                                        { key: 'name', label: 'Teacher Name' },
                                                        { key: 'present', label: 'Present' },
                                                        { key: 'absent', label: 'Absent' },
                                                        { key: 'late', label: 'Late' },
                                                        { key: 'leave', label: 'Leave' },
                                                        { key: 'missed', label: 'Unmarked' },
                                                        { key: 'total', label: 'Days Recorded' },
                                                        { key: 'percentage', label: 'Attendance %' },
                                                    ].map(({ key, label }) => (
                                                        <th
                                                            key={key}
                                                            className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                                            onClick={() => handleSort(key)}
                                                        >
                                                            <div className={`flex items-center ${['name'].includes(key) ? '' : 'justify-center'}`}>
                                                                {label} <SortIcon columnKey={key} />
                                                            </div>
                                                        </th>
                                                    ))}
                                                    <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200 bg-white">
                                                {filteredAndSortedTeachers.map((teacher) => {
                                                    const expected = teacher.total + (teacher.missed || 0);
                                                    const attendanceRate = getPercentage(teacher.present, expected);
                                                    return (
                                                        <tr key={teacher.id} className="hover:bg-slate-50">
                                                            <td className="px-3 py-3 text-sm font-medium text-slate-900">
                                                                <div className="flex items-center gap-3">
                                                                    <PhotoThumb
                                                                        src={resolvePersonPhoto(teacher, 'teacher')}
                                                                        sizeClass="h-8 w-8"
                                                                        onClick={() => setPreviewSrc(resolvePersonPhoto(teacher, 'teacher'))}
                                                                    />
                                                                    {teacher.name}
                                                                </div>
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">{teacher.present}</span></td>
                                                            <td className="px-3 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800">{teacher.absent}</span></td>
                                                            <td className="px-3 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{teacher.late}</span></td>
                                                            <td className="px-3 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{teacher.leave}</span></td>
                                                            <td className="px-3 py-3 text-sm text-center"><span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500">{teacher.missed || 0}</span></td>
                                                            <td className="px-3 py-3 text-sm text-center font-semibold text-slate-700">{teacher.total}</td>
                                                            <td className="px-3 py-3 text-sm text-center">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${parseFloat(attendanceRate) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                                                                    parseFloat(attendanceRate) >= 75 ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'}`}>
                                                                    {attendanceRate}%
                                                                </span>
                                                            </td>
                                                            <td className="px-3 py-3 text-sm text-center">
                                                                <Button variant="secondary" onClick={() => { setSelectedTeacher({ id: teacher.id, name: teacher.name, photo: teacher.photo }); setShowDailyRecords(true); }} className="text-xs">
                                                                    View Details
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                                {filteredAndSortedTeachers.length === 0 && (
                                                    <tr><td colSpan={9} className="px-3 py-6 text-center text-sm text-slate-500">No teachers found</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile Cards for Browse Mode */}
                                    <div className="md:hidden space-y-2.5">
                                        {filteredAndSortedTeachers.map((teacher) => {
                                            const expected = teacher.total + (teacher.missed || 0);
                                            const attendanceRate = getPercentage(teacher.present, expected);

                                            return (
                                                <div key={teacher.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-2.5">
                                                            <PhotoThumb
                                                                src={resolvePersonPhoto(teacher, 'teacher')}
                                                                sizeClass="h-9 w-9"
                                                                onClick={() => setPreviewSrc(resolvePersonPhoto(teacher, 'teacher'))}
                                                            />
                                                            <div>
                                                                <div className="text-xs font-bold text-slate-900">{teacher.name}</div>
                                                                <div className="text-[10px] text-slate-400">Teacher</div>
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => { setSelectedTeacher({ id: teacher.id, name: teacher.name, photo: teacher.photo }); setShowDailyRecords(true); }}
                                                            className="text-xs py-1 px-2.5 h-auto"
                                                        >
                                                            Details
                                                        </Button>
                                                    </div>

                                                    {timeFilterMode === 'day' ? (
                                                        <div className="pt-1.5 flex items-center justify-between border-t border-slate-100 text-xs">
                                                            <span className="text-slate-500 font-medium">Status</span>
                                                            {teacher.present > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                                    PRESENT
                                                                </span>
                                                            ) : teacher.absent > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                                                    ABSENT
                                                                </span>
                                                            ) : teacher.late > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                                    LATE
                                                                </span>
                                                            ) : teacher.leave > 0 ? (
                                                                <span className="px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                                                    LEAVE
                                                                </span>
                                                            ) : (
                                                                <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200">
                                                                    UNMARKED
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="space-y-1.5 pt-1.5 border-t border-slate-100">
                                                            <div className="flex items-center justify-between text-xs">
                                                                <span className="text-[10px] text-slate-400 font-medium">Attendance Rate</span>
                                                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                                                    parseFloat(attendanceRate) >= 90 ? 'bg-emerald-100 text-emerald-800' :
                                                                    parseFloat(attendanceRate) >= 75 ? 'bg-amber-100 text-amber-800' :
                                                                    'bg-rose-100 text-rose-800'
                                                                }`}>
                                                                    {attendanceRate}%
                                                                </span>
                                                            </div>
                                                            <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden flex">
                                                                {teacher.present > 0 && <div className="h-full bg-emerald-500" style={{ width: `${(teacher.present / (expected || 1)) * 100}%` }} />}
                                                                {teacher.absent > 0 && <div className="h-full bg-rose-500" style={{ width: `${(teacher.absent / (expected || 1)) * 100}%` }} />}
                                                                {teacher.late > 0 && <div className="h-full bg-amber-500" style={{ width: `${(teacher.late / (expected || 1)) * 100}%` }} />}
                                                                {teacher.leave > 0 && <div className="h-full bg-blue-500" style={{ width: `${(teacher.leave / (expected || 1)) * 100}%` }} />}
                                                                {(teacher.missed || 0) > 0 && <div className="h-full bg-slate-400" style={{ width: `${((teacher.missed || 0) / (expected || 1)) * 100}%` }} />}
                                                            </div>
                                                            <div className="flex justify-between text-[10px] text-slate-500 pt-0.5">
                                                                <span className="text-emerald-700 font-semibold">P: {teacher.present}</span>
                                                                <span className="text-rose-700 font-semibold">A: {teacher.absent}</span>
                                                                <span className="text-amber-700 font-semibold">L: {teacher.late}</span>
                                                                <span className="text-blue-700 font-semibold">Lv: {teacher.leave}</span>
                                                                <span className="text-slate-400">Days: {teacher.total}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {filteredAndSortedTeachers.length === 0 && (
                                            <div className="p-6 text-center text-sm text-slate-500 bg-slate-50 rounded-xl border">
                                                No teachers found
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </Card>
                    </div>
                )}

                {isLoading && (
                    <Card>
                        <div className="py-12 text-center"><p className="text-slate-500">Loading attendance data...</p></div>
                    </Card>
                )}
            </div>

            <Modal
                isOpen={showDailyRecords}
                onClose={() => { setShowDailyRecords(false); setSelectedTeacher(null); }}
                title={selectedTeacher ? `Attendance Details - ${selectedTeacher.name}` : 'Attendance Details'}
                panelClassName="sm:max-w-5xl"
            >
                {selectedTeacher && (
                    <TeacherDetailContent
                        teacher={selectedTeacher}
                        availableYears={availableYears}
                    />
                )}
            </Modal>
            <PhotoLightbox src={previewSrc} onClose={() => setPreviewSrc(null)} />
        </DashboardShell>
    );
}
